import React, { useEffect, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import PageHeading from "components/shared/PageHeading";
import AddClassModal from "components/modals/AddClassModal";
import CustomPagination from "components/shared/Pagination";
import EntityTable from "components/shared/EntityTable";
import {
  entityTitleColumn,
  memberCountColumn,
} from "components/shared/entityColumns";
import {
  getAllClasses,
  deleteClass,
  getArchivedClasses,
  restoreClass,
  permanentlyDeleteClass,
} from "services/Teacher/Class.service";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUndo,
  faTrash,
  faAdd,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DeleteSchoolModal from "components/modals/DeleteSchoolModal";
import { useAuth } from "utils/AuthContext";

// view: "classes" | "groups" | "all" — /classes and /groups render one section
// each; "all" keeps the original combined page available.
const ClassesAndGroups = ({ view = "all" }) => {
  const { t } = useTranslation();
  const { userRoles } = useAuth();
  const showClasses = view === "all" || view === "classes";
  const showGroups = view === "all" || view === "groups";
  const pageTitle =
    view === "classes" ? "Classes" : view === "groups" ? "Groups" : "Classes & Groups";
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("class"); // Default type for modal
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "class" or "group"
  const [isPermanentDelete, setIsPermanentDelete] = useState(false); // Track if it's permanent delete
  const [showArchived, setShowArchived] = useState(false); // Toggle for archived view
  const [archivedClasses, setArchivedClasses] = useState([]);
  const [archivedGroups, setArchivedGroups] = useState([]);
  const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);
  const [archivedTotalPages, setArchivedTotalPages] = useState(1);
  const navigate = useNavigate();

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleShowDeleteModal = (item, type, permanent = false) => {
    setItemToDelete(item);
    setDeleteType(type);
    setIsPermanentDelete(permanent);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteType("");
    setIsPermanentDelete(false);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    const action = isPermanentDelete ? "Permanently deleting" : "Archiving";
    const successAction = isPermanentDelete ? "permanently deleted" : "archived";

    const toastId = toast.loading(
      t(
        deleteType === "class"
          ? `${action} class...`
          : `${action} group...`
      )
    );

    try {
      if (isPermanentDelete) {
        await permanentlyDeleteClass(itemToDelete._id);
      } else {
        await deleteClass(itemToDelete._id);
      }

      toast.update(toastId, {
        render: t(
          deleteType === "class"
            ? `Class ${successAction} successfully!`
            : `Group ${successAction} successfully!`
        ),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      handleCloseDeleteModal();
      fetchClasses(); // Refresh the list
      if (showArchived) {
        fetchArchivedClasses(archivedCurrentPage); // Refresh archived list if viewing archived
      }
    } catch (error) {
      console.error("Error:", error);
      toast.update(toastId, {
        render: t(`Error ${action.toLowerCase()}. Please try again.`),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const fetchArchivedClasses = async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await getArchivedClasses(page);
      const allArchivedItems = response?.data?.results?.archivedClasses || [];
      const totalPages = response?.data?.results?.totalPages || 1;
      const currentPage = response?.data?.results?.currentPage || page;
      
      // Filter by type
      const archivedClasses = allArchivedItems.filter(item => item.type === "class");
      const archivedGroups = allArchivedItems.filter(item => item.type === "group");

      setArchivedClasses(archivedClasses);
      setArchivedGroups(archivedGroups);
      setArchivedTotalPages(totalPages);
      setArchivedCurrentPage(currentPage);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching archived classes:", error);
    }
  };

  const handleRestoreClass = async (item, type) => {
    const toastId = toast.loading(
      t(type === "class" ? "Restoring class..." : "Restoring group...")
    );

    try {
      await restoreClass(item._id);

      toast.update(toastId, {
        render: t(
          type === "class"
            ? "Class restored successfully!"
            : "Group restored successfully!"
        ),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      fetchClasses();
      fetchArchivedClasses(archivedCurrentPage);
    } catch (error) {
      console.error("Error restoring:", error);
      toast.update(toastId, {
        render: t("Error restoring. Please try again."),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const handleToggleArchived = () => {
    if (!showArchived) {
      setArchivedCurrentPage(1);
      fetchArchivedClasses(1);
    }
    setShowArchived(!showArchived);
  };

  const fetchClasses = async () => {
    setIsLoading(true);
    try {
      const response = await getAllClasses();
      const classes = response?.data?.result?.classes?.data || [];
      const groups = response?.data?.result?.groups?.data || [];

      // Sort classes alphabetically by title
      const sortedClasses = classes.sort((a, b) =>
        a.title.localeCompare(b.title)
      );

      // Sort groups alphabetically by title
      const sortedGroups = groups.sort((a, b) =>
        a.title.localeCompare(b.title)
      );

      setClasses(sortedClasses);
      setGroups(sortedGroups);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching classes and groups:", error);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  const handleArchivedPageChange = (page) => {
    setArchivedCurrentPage(page);
    fetchArchivedClasses(page);
  };

  // "2 days ago" style label, matching the exercise list pages.
  const formatRelativeTime = (date) => {
    if (!date) return "-";
    const then = new Date(date).getTime();
    if (Number.isNaN(then)) return "-";
    const days = Math.floor((Date.now() - then) / 86400000);
    if (days < 1) return t("Today");
    if (days === 1) return t("Yesterday");
    if (days < 7) return `${days} ${t("days ago")}`;
    if (days < 30) {
      const weeks = Math.floor(days / 7);
      return `${weeks} ${weeks === 1 ? t("week ago") : t("weeks ago")}`;
    }
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? t("month ago") : t("months ago")}`;
  };

  const teacherNames = (item) =>
    (item?.teachers || [])
      .map((teacher) => teacher?.fullName)
      .filter(Boolean);

  /**
   * One table for classes or groups. `type` drives the icon, the empty state
   * and which handlers the row actions call.
   */
  const renderTable = (items, type) => {
    const isGroup = type === "group";
    const emptyLabel = showArchived
      ? isGroup
        ? t("No archived groups")
        : t("No archived classes")
      : isGroup
        ? t("No groups created yet")
        : t("No classes created yet");

    const canManageArchived =
      userRoles === "superUser" || userRoles === "admin";

    return (
      <EntityTable
        loading={isLoading}
        loadingRows={3}
        items={items}
        emptyLabel={emptyLabel}
        onRowClick={(item) => navigate(item?._id)}
        columns={[
          entityTitleColumn(t, {
            isGroup,
            label: isGroup ? "Group" : "Class",
          }),
          memberCountColumn(t),
          {
            key: "teachers",
            label: "Teachers",
            skeleton: <Skeleton width={120} />,
            render: (item) => {
              const names = teacherNames(item);
              return (
                <span
                  className="text-muted"
                  style={{ fontSize: "13px" }}
                  title={names.join(", ")}
                >
                  {names.length === 0
                    ? "-"
                    : names.length <= 2
                      ? names.join(", ")
                      : `${names.slice(0, 2).join(", ")} +${names.length - 2}`}
                </span>
              );
            },
          },
          {
            key: "created",
            label: "Created",
            skeleton: <Skeleton width={80} />,
            render: (item) => (
              <span
                className="text-muted"
                style={{ fontSize: "13px", whiteSpace: "nowrap" }}
              >
                {formatRelativeTime(item?.createdAt)}
              </span>
            ),
          },
          {
            key: "actions",
            label: "Actions",
            thClassName: "py-3 pe-3 fw-semibold text-end",
            className: "py-3 pe-3",
            stopRowClick: true,
            skeleton: <Skeleton width={40} />,
            render: (item) => (
              <div className="d-flex align-items-center gap-2 justify-content-end">
                {showArchived ? (
                  canManageArchived && (
                    <>
                      <Button
                        size="sm"
                        className="icon-action-btn icon-action-restore"
                        onClick={() => handleRestoreClass(item, type)}
                        title={isGroup ? t("Restore group") : t("Restore class")}
                      >
                        <FontAwesomeIcon icon={faUndo} />
                      </Button>
                      <Button
                        size="sm"
                        className="icon-action-btn icon-action-danger"
                        onClick={() => handleShowDeleteModal(item, type, true)}
                        title={
                          isGroup
                            ? t("Permanently delete group")
                            : t("Permanently delete class")
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </>
                  )
                ) : (
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      className="icon-action-btn e-caret-hide"
                      title={t("More actions")}
                      aria-label={t("More actions")}
                      variant="link"
                    >
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      className="border-0"
                      style={{
                        boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                      }}
                    >
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => handleShowDeleteModal(item, type)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        {t("Delete")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            ),
          },
        ]}
      />
    );
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <PageHeading
            heading={
              showArchived && view !== "all"
                ? t(`Archived ${pageTitle}`)
                : t(pageTitle)
            }
          />
          {/* Same grey subtitle the exercise/people list pages use. */}
          <p className="exercise-header-subtitle">
            {view === "classes"
              ? t("Organise your students into classes and assign them work.")
              : view === "groups"
                ? t("Group students across classes to assign work to a subset.")
                : t("Manage the classes and groups at your school.")}
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {(userRoles === "superUser" || userRoles === "admin") && (
            <Button
              variant={showArchived ? "secondary" : "outline-secondary"}
              onClick={handleToggleArchived}
              className="px-3 py-2"
            >
              {showArchived ? t("View Active") : t("View Archived")}
            </Button>
          )}
          <Dropdown>
            <Dropdown.Toggle
              className="px-3 py-2 border-0 button-style d-flex align-items-center gap-1"
            >
              <FontAwesomeIcon className="me-2" icon={faAdd} />
              {t("Add Student")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => navigate("/users/students?studentmodal=true")}>
                {t("Add Single")}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => navigate("/users/students?importsheetmodal=true")}>
                {t("Import Sheet")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {/* On a single-section page the create action belongs in the header. */}
          {!showArchived && view === "classes" && (
            <Button
              className="px-3 py-2 border-0 button-style"
              onClick={() => {
                setShowModal(true);
                setModalType("class");
              }}
            >
              {t("Create new class")}
            </Button>
          )}
          {!showArchived && view === "groups" && (
            <Button
              className="px-3 py-2 border-0 button-style"
              onClick={() => {
                setShowModal(true);
                setModalType("group");
              }}
            >
              {t("Create new group")}
            </Button>
          )}
        </div>
      </div>
      <div className="mt-4">
        {/* Classes Section */}
        {showClasses && (
        <>
        {view === "all" && (
          <div className="d-flex justify-content-between mt-4">
            <div>
              <PageHeading
                heading={showArchived ? t("Archived Classes") : t("Classes")}
              />
            </div>
            {!showArchived && (
              <div>
                <Button
                  className="px-3 py-2 border-0 button-style"
                  onClick={() => {
                    setShowModal(true);
                    setModalType("class"); // Set modal type to "class"
                  }}
                >
                  {t("Create new class")}
                </Button>
              </div>
            )}
          </div>
        )}
        {renderTable(showArchived ? archivedClasses : classes, "class")}
        </>
        )}

        {/* Groups Section */}
        {showGroups && (
        <>
        {view === "all" && (
          <div className="d-flex justify-content-between mt-4">
            <div>
              <PageHeading
                heading={showArchived ? t("Archived Groups") : t("Groups")}
              />
            </div>
            {!showArchived && (
              <div>
                <Button
                  className="px-3 py-2 border-0 button-style"
                  onClick={() => {
                    setShowModal(true);
                    setModalType("group"); // Set modal type to "group"
                  }}
                >
                  {t("Create new group")}
                </Button>
              </div>
            )}
          </div>
        )}
        {renderTable(showArchived ? archivedGroups : groups, "group")}
        </>
        )}

        {showArchived && archivedTotalPages > 1 && (
          <CustomPagination
            currentPage={archivedCurrentPage}
            totalPages={archivedTotalPages}
            onPageChange={handleArchivedPageChange}
          />
        )}
      </div>

      {/* Add Class or Group Modal */}
      <AddClassModal
        show={showModal}
        handleClose={handleCloseModal}
        fetchClasses={fetchClasses}
        type={modalType} // Pass the type dynamically
      />

      {/* Archive/Delete Confirmation Modal */}
      <DeleteSchoolModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirmDelete={handleDeleteConfirm}
        isPermanent={isPermanentDelete}
      />
    </>
  );
};

export default ClassesAndGroups;
